import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { DataService } from 'src/app/data.service';

/**
 *  TANMENET – feltöltés pedagógusnak, összesítő adminnak.
 *
 *  Eddig egy n8n-folyamat töltötte fel a tanmenetet; ez a képernyő váltja ki. Az adat
 *  ugyanoda kerül, ahonnan a napló dolgozik: (osztály, tantárgy, óraszám) hármas, ahol
 *  az óraszám a tanév eleje óta megtartott órák sorszáma – így a napló a megfelelő
 *  órához kínálja fel a témát.
 *
 *  Ugyanez a komponens szolgálja ki a pedagógusi és az admin nézetet. A különbség nem
 *  a felületen dől el: a szerver adja vissza, hogy a belépett felhasználó admin-e, és
 *  ő szűkíti a párok listáját is. A felület csak megjeleníti, amit kap – így a
 *  jogosultság nem kerülhető meg a kliens megbuherálásával.
 */
@Component({
  selector: 'app-tanmenet',
  templateUrl: './tanmenet.component.html',
  styleUrls: ['./tanmenet.component.scss'],
})
export class TanmenetComponent implements OnInit {

  intezmeny = '';
  /** A belépett dolgozó azonosítója. A szerver a TOKENT részesíti előnyben – ez csak
   *  ott számít, ahol nincs kikényszerítve a tokenes hitelesítés (dev, standalone). */
  allomany = '';
  tanev: number | null = null;
  tanevek: number[] = [];
  /** A szerver adja vissza: adminként az egész intézményt látja, nem csak a saját párjait. */
  admin = false;

  parok: any[] = [];
  osszesito: any = null;
  toltodik = false;
  hiba = '';
  szuro = '';

  /* ── feltöltés ── */
  kivalasztott: any = null;
  sorok: { oraszam: number; tema: string }[] = [];
  fajlNev = '';
  mentesFut = false;

  /* ── megtekintés ── */
  nezettPar: any = null;
  nezettTetelek: any[] | null = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = sajat?.intezmenyId || '';
    const um = await this.data.getFromStorage('userMetadata');
    this.allomany = String(um?.adid || um?.username || um?.preferred_username || '').trim();

    try {
      const t = JSON.parse(await this.data.getFromStorage('tanev'));
      const aktualis = Number(t?.aktualistanev);
      const elso = Number(t?.elsotanev) || aktualis - 3;
      this.tanev = aktualis;
      // Csak a már elkezdődött tanévek: jövőbeli évre nincs mit feltölteni.
      for (let e = aktualis; e >= elso; e--) this.tanevek.push(e);
    } catch { this.tanev = new Date().getFullYear(); this.tanevek = [this.tanev]; }

    await this.betolt();
  }

  async betolt() {
    if (!this.intezmeny) {
      this.hiba = 'Nincs kiválasztott intézmény. Válassz intézményt a fejlécben, vagy lépj ki és be.';
      return;
    }
    this.toltodik = true;
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/tanmenet/sajat/${this.intezmeny}?tanev=${this.tanev}`
        + `&allomany=${encodeURIComponent(this.allomany)}`));
      this.parok = r?.parok || [];
      this.admin = !!r?.admin;
      this.osszesito = null;
      // Az összesítő az admin JOGOSULTSÁGHOZ kötődik, nem az útvonalhoz: ha admin
      // a saját menüjéből nyitja meg, ugyanúgy a teljes képet kapja.
      if (this.admin) {
        this.osszesito = await firstValueFrom(this.http.get(
          `/api/tanmenet/osszesito/${this.intezmeny}?tanev=${this.tanev}`));
        this.parok = this.osszesito?.tetelek || this.parok;
      }
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A tanmenetek betöltése nem sikerült.';
      this.parok = [];
    } finally {
      this.toltodik = false;
    }
  }

  get szurtParok(): any[] {
    const k = this.szuro.trim().toLowerCase();
    if (!k) return this.parok;
    //  A csoport neve és a tagok osztályai is kereshetők: az osztály nélküli csoportnak
    //  (sportkör) nincs osztályneve, a kolléga a csoport nevére vagy egy osztályra keres.
    return this.parok.filter((p) =>
      `${p.osztaly_nev || ''} ${p.csoport_nev || ''} ${(p.csoport_osztalyok || []).join(' ')} `
      + `${p.tantargy_nev} ${p.tanar_nev || ''}`.toLowerCase().includes(k));
  }

  /* ── Excel beolvasása ─────────────────────────────────────────────────────
   *  A tanmenet-táblák eltérő fejléccel készülnek, ezért nem egy fix oszlopnevet
   *  keresünk: a téma és az óraszám oszlopát több szokásos elnevezés alapján is
   *  felismerjük, és ha óraszám-oszlop nincs, a sorrend adja a sorszámot.
   */
  private static readonly TEMA_OSZLOP = ['téma', 'tema', 'témakör', 'temakor', 'tananyag', 'óra témája', 'anyag'];
  private static readonly ORA_OSZLOP = ['óraszám', 'oraszam', 'sorszám', 'sorszam', 'óra', 'ora', 'sor'];

  fajlValtozott(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.fajlNev = file.name;
    const olvaso = new FileReader();
    olvaso.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const lap = wb.Sheets[wb.SheetNames[0]];
        const nyers: any[] = XLSX.utils.sheet_to_json(lap, { raw: true, defval: null });
        this.sorok = this.sorokBol(nyers);
        if (!this.sorok.length) {
          this.data.sendToast('A fájlban nem találtam témát. Kell egy „Téma" (vagy „Tananyag") '
            + 'fejlécű oszlop, alatta a témákkal.', 'error');
        }
      } catch {
        this.data.sendToast('A fájlt nem sikerült beolvasni. Excel (.xlsx/.xls) vagy CSV kell.', 'error');
        this.sorok = [];
      }
    };
    olvaso.readAsBinaryString(file);
  }

  private sorokBol(nyers: any[]): { oraszam: number; tema: string }[] {
    if (!nyers.length) return [];
    const fejlecek = Object.keys(nyers[0] || {});
    const talal = (jeloltek: string[]) =>
      fejlecek.find((f) => jeloltek.includes(String(f).trim().toLowerCase()));

    const temaOszlop = talal(TanmenetComponent.TEMA_OSZLOP)
      // Ha egyik szokásos név sem stimmel, az első olyan oszlop, amiben szöveg van.
      || fejlecek.find((f) => nyers.some((r) => typeof r[f] === 'string' && r[f].trim()));
    if (!temaOszlop) return [];
    const oraOszlop = talal(TanmenetComponent.ORA_OSZLOP);

    const ki: { oraszam: number; tema: string }[] = [];
    for (const r of nyers) {
      const tema = String(r[temaOszlop] ?? '').trim();
      if (!tema) continue;
      const szam = oraOszlop ? Number(r[oraOszlop]) : NaN;
      ki.push({ oraszam: Number.isFinite(szam) && szam > 0 ? szam : ki.length + 1, tema });
    }
    return ki;
  }

  feltoltestKezd(par: any) {
    this.kivalasztott = par;
    this.sorok = [];
    this.fajlNev = '';
    this.nezettPar = null;
    this.nezettTetelek = null;
    this.egeszEvfolyam = false;
  }

  /*  Az évfolyamra szóló feltöltés csak osztályhoz kötött párnál értelmes. Az osztály
   *  nélküli csoportnak (sportkör: több évfolyam tanulói egy csoportban) nincs
   *  évfolyama – ott a kapcsoló nem is jelenik meg, a szerver pedig nem is fogadja el. */
  evfolyamKapcsolhato(par: any): boolean { return !!par?.osztaly; }

  megsem() { this.kivalasztott = null; this.sorok = []; this.fajlNev = ''; }

  nezetBezar() { this.nezettPar = null; this.nezettTetelek = null; }

  /*  HATÓKÖR. Nyelvi bontásnál a csoport a helyes egység (más tanár, más ütem),
   *  matematikánál viszont az évfolyam együtt halad, és fölösleges osztályonként
   *  ugyanazt feltölteni. A tantárgyból ezt nem lehet kitalálni – a feltöltő tudja. */
  egeszEvfolyam = false;

  async mentes() {
    if (!this.kivalasztott || !this.sorok.length) return;
    /*  A megerősítés csak a SAJÁT tanmenetre vonatkozik. Az öröklött (osztályszintű)
     *  tanmenetet a feltöltés nem cseréli le: a csoport kap egy sajátot, ami erősebb –
     *  a többi csoporté érintetlen marad. */
    const evfolyamra = this.egeszEvfolyam && this.evfolyamKapcsolhato(this.kivalasztott);
    if (evfolyamra && !confirm(
      `A tanmenet az EGÉSZ ÉVFOLYAMRA kerül fel: minden olyan osztályra, ahol ezt a `
      + `tantárgyat tanítják — nem csak a saját csoportodra.\n\n`
      + 'Osztályszinten írjuk be, tehát amelyik csoportnak van saját tanmenete, azt nem '
      + 'bántja. Folytatod?')) return;
    if (!evfolyamra && this.kivalasztott.sajat_tanmenet && !confirm(
      `A(z) ${this.parNev(this.kivalasztott)} párhoz már van `
      + `tanmenet (${this.kivalasztott.tanmenet_orak} óra). A feltöltés a RÉGIT LECSERÉLI. Folytatod?`)) return;

    this.mentesFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/tanmenet/feltoltes', {
        intezmeny: this.intezmeny,
        allomany: this.allomany,
        tanev: this.tanev,
        tantargy: this.kivalasztott.tantargy,
        //  Osztály nélküli csoportnál (sportkör) nincs osztály: a pár a csoporté.
        osztaly: this.kivalasztott.osztaly || null,
        //  A CSOPORT is megy: enélkül a feltöltés az osztály összes csoportjára szólna,
        //  pedig azokat más tanár tartja, más ütemben.
        csoport: this.kivalasztott.csoport || null,
        hatokor: evfolyamra ? 'evfolyam' : 'csoport',
        temak: this.sorok,
      }));
      this.data.sendToast(
        (r.hatokor === 'evfolyam'
          ? `${r.oraszam} óra feltöltve a(z) ${r.evfolyam}. évfolyam ${r.osztalyok} osztályára`
          : `${r.felvett} óra feltöltve`)
        + (r.torolt ? ` (${r.torolt} korábbi sor lecserélve).` : '.'), 'success');
      this.egeszEvfolyam = false;
      this.megsem();
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A feltöltés nem sikerült.', 'error');
    } finally {
      this.mentesFut = false;
    }
  }

  /*  A pár olvasható neve. Csoportos óránál a CSOPORT a lényeg: „12/a – Angol" három
   *  sorban ugyanaz volna, holott három különböző tanmenetről van szó. */
  /*  A pár neve: bontott órán a CSOPORT, egyébként az osztály. Az osztálynév csoportos
   *  órán csak egy a több kötött közül, ezért nem nevezzük meg – lásd a táblázat
   *  oszlopánál a részletes indoklást. */
  parNev(par: any): string {
    const fej = par?.csoport_nev || par?.osztaly_nev || '';
    return `${fej} – ${par?.tantargy_nev || ''}`;
  }

  async megnez(par: any) {
    this.kivalasztott = null;
    this.nezettPar = par;
    this.nezettTetelek = null;
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/tanmenet/tetelek/${this.intezmeny}?tanev=${this.tanev}`
        + `&tantargy=${encodeURIComponent(par.tantargy)}`
        + (par.osztaly ? `&osztaly=${encodeURIComponent(par.osztaly)}` : '')
        + (par.csoport ? `&csoport=${encodeURIComponent(par.csoport)}` : '')));
      this.nezettTetelek = r?.tetelek || [];
    } catch {
      this.nezettTetelek = [];
      this.data.sendToast('A tanmenet nem tölthető be.', 'error');
    }
  }

  async torol(par: any) {
    if (par.orokolt_tanmenet) {
      this.data.sendToast('Ez a tanmenet osztályszintű, nem ehhez a csoporthoz készült – '
        + 'innen nem törölhető.', 'warning');
      return;
    }
    if (!confirm(`Törlöd a(z) ${this.parNev(par)} tanmenetét `
      + `(${par.tanmenet_orak} óra)? A napló ezután nem kínál témát ezekhez az órákhoz.`)) return;
    try {
      //  Az útvonalban nem lehet üres szakasz: osztály nélküli csoportnál (sportkör)
      //  `-` áll az osztály helyén, a szerver ezt NULL-nak veszi.
      await firstValueFrom(this.http.delete(
        `/api/tanmenet/${this.tanev}/${encodeURIComponent(par.tantargy)}`
        + `/${par.osztaly ? encodeURIComponent(par.osztaly) : '-'}/${this.intezmeny}?allomany=${encodeURIComponent(this.allomany)}`
        + (par.csoport ? `&csoport=${encodeURIComponent(par.csoport)}` : '')));
      this.data.sendToast('A tanmenet törölve.', 'success');
      this.nezettPar = null;
      this.nezettTetelek = null;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }
}
